/**
 * PatchSwitcherController - Handles patch switcher dropdown and related menu actions
 * 
 * Manages the patch switcher dropdown (recent patches list, quick actions),
 * and menu actions like New, Open, Save As, etc.
 */

import { escapeHtml, formatTimestamp } from '../utils/index.js';
import { eventBus, EVENTS } from '../services/EventBus.js';
import { showToast } from './ToastController.js';
import { closePatchSwitcher } from './MenuController.js';

// Module state - injected via init
let storageService = null;
let recentPatchesService = null;
let loadAndApplyPatchFn = null;
let setCurrentPatchNameFn = null;
let getCurrentPatchNameFn = null;
let showConfirmModalFn = null;
let getInstanceCountFn = null;
let getConnectionCountFn = null;
let newPatchFn = null;
let showSaveAsModalFn = null;
let showOpenStorageModalFn = null;
let showSaveVersionModalFn = null;
let showVersionHistoryModalFn = null;
let saveStateFn = null;
let importFromFileFn = null;
let showPatchSettingsModalFn = null;

// Cached patches for sync rendering
let _patchesCache = [];
let _patchesCacheLoaded = false;

/**
 * Initialize the PatchSwitcherController
 * @param {Object} deps - Dependencies
 * @param {Object} deps.storageService - Storage service instance
 * @param {Object} deps.recentPatchesService - Recent patches service
 * @param {Function} deps.loadAndApplyPatch - Callback to load and apply a patch
 * @param {Function} deps.setCurrentPatchName - Callback to set current patch name
 * @param {Function} deps.getCurrentPatchName - Function to get current patch name
 * @param {Function} deps.showConfirmModal - Function to show confirmation modal
 * @param {Function} deps.getInstanceCount - Function to get device instance count
 * @param {Function} deps.getConnectionCount - Function to get connection count
 * @param {Function} deps.newPatch - Callback to create a new patch
 * @param {Function} deps.showSaveAsModal - Callback to show Save As modal
 * @param {Function} deps.showOpenStorageModal - Callback to show Open Storage modal
 * @param {Function} deps.showSaveVersionModal - Callback to show Save Version modal
 * @param {Function} deps.showVersionHistoryModal - Callback to show Version History modal
 * @param {Function} deps.saveState - Callback to save state to file (.pw export)
 * @param {Function} deps.importFromFile - Callback to import from file
 * @param {Function} deps.showPatchSettingsModal - Callback to show patch settings modal
 * @returns {Object} Public methods for onclick handlers
 */
export function initPatchSwitcherController(deps) {
    storageService = deps.storageService;
    recentPatchesService = deps.recentPatchesService;
    loadAndApplyPatchFn = deps.loadAndApplyPatch;
    setCurrentPatchNameFn = deps.setCurrentPatchName;
    getCurrentPatchNameFn = deps.getCurrentPatchName;
    showConfirmModalFn = deps.showConfirmModal;
    getInstanceCountFn = deps.getInstanceCount;
    getConnectionCountFn = deps.getConnectionCount;
    newPatchFn = deps.newPatch;
    showSaveAsModalFn = deps.showSaveAsModal;
    showOpenStorageModalFn = deps.showOpenStorageModal;
    showSaveVersionModalFn = deps.showSaveVersionModal;
    showVersionHistoryModalFn = deps.showVersionHistoryModal;
    saveStateFn = deps.saveState;
    importFromFileFn = deps.importFromFile;
    showPatchSettingsModalFn = deps.showPatchSettingsModal;
    
    // Subscribe to events to refresh list
    eventBus.on(EVENTS.PATCH_SAVED, () => {
        invalidatePatchesCache();
        renderPatchSwitcherList();
    });
    
    eventBus.on(EVENTS.PATCH_LOADED, () => {
        renderPatchSwitcherList();
    });
    
    // Return public methods for window/onclick exposure
    return {
        renderPatchSwitcherList,
        switchToPatch,
        deletePatchFromSwitcher,
        newPatchFromSwitcher,
        invalidatePatchesCache,
        // Menu action handlers
        newPatchFromMenu,
        saveAsFromMenu,
        openFromMenu,
        saveVersionFromMenu,
        showVersionHistoryFromMenu,
        saveToFileFromMenu,
        saveToClipboardFromMenu,
        importFromFileFromMenu,
        importFromClipboardFromMenu,
        exportShareCodeFromMenu,
        importShareCodeFromMenu,
        switchToPatchFromMenu,
        patchSettingsFromMenu
    };
}

// ============= PATCH CACHE MANAGEMENT =============

/**
 * Invalidate patches cache (call on save/delete/auth change)
 */
function invalidatePatchesCache() {
    _patchesCacheLoaded = false;
}

/**
 * Get effective patches from cache (sync, returns cached or triggers async load)
 * @returns {Array} Cached patches array
 */
function getEffectivePatches() {
    const isCloud = storageService.isCloud();
    
    if (!_patchesCacheLoaded) {
        // Trigger async load and return empty for now
        loadPatchesForSwitcher();
        return [];
    }
    
    return _patchesCache.map(p => ({
        id: p.id,
        name: p.name,
        deviceCount: p.deviceCount || 0,
        connectionCount: p.connectionCount || 0,
        timestamp: p.timestamp,
        isCloud: isCloud
    }));
}

/**
 * Async load patches for switcher
 */
async function loadPatchesForSwitcher() {
    if (!storageService) return;
    _patchesCache = await storageService.listPatches();
    _patchesCacheLoaded = true;
    renderPatchSwitcherList();
}

// ============= PATCH SWITCHER RENDERING =============

/**
 * Render the patch switcher dropdown list
 */
function renderPatchSwitcherList() {
    const menuEl = document.getElementById('patch-switcher-menu');
    const list = document.getElementById('patch-switcher-list');
    
    if (!menuEl || !list) return;
    
    const currentPatchName = getCurrentPatchNameFn();
    
    // Build menu section
    let menuHtml = '';
    
    // Menu items - autosave is always on, version saving is explicit
    menuHtml += `
        <div class="patch-switcher-menu-item" onclick="saveVersionFromMenu(event)">
            <span class="icon">📌</span> Save Version...
        </div>
        <div class="patch-switcher-menu-item" onclick="showVersionHistoryFromMenu(event)">
            <span class="icon">🕐</span> Version History...
        </div>
        <div class="patch-switcher-menu-divider"></div>
        <div class="patch-switcher-menu-item" onclick="newPatchFromMenu(event)">
            <span class="icon">📄</span> New
        </div>
        <div class="patch-switcher-menu-item" onclick="openFromMenu(event)">
            <span class="icon">📂</span> Open...
        </div>
        <div class="patch-switcher-menu-item" onclick="saveAsFromMenu(event)">
            <span class="icon">💾</span> Save As...
        </div>
        <div class="patch-switcher-menu-item" onclick="patchSettingsFromMenu(event)">
            <span class="icon">⚙️</span> Patch Settings...
        </div>
        <div class="patch-switcher-menu-divider"></div>
        <div class="patch-switcher-menu-item" onclick="saveToFileFromMenu(event)">
            <span class="icon">📤</span> Export to File (.pw)
        </div>
        <div class="patch-switcher-menu-item" onclick="exportShareCodeFromMenu(event)">
            <span class="icon">📋</span> Copy to Clipboard
        </div>
        <div class="patch-switcher-menu-item" onclick="importFromFileFromMenu(event)">
            <span class="icon">📥</span> Import from File
        </div>
        <div class="patch-switcher-menu-item" onclick="importFromClipboardFromMenu(event)">
            <span class="icon">📋</span> Paste from Clipboard
        </div>
    `;
    
    // Recent patches section (last 5 recently opened/saved, excluding current patch)
    const recentPatches = recentPatchesService.getRecentPatches()
        .filter(p => p.name !== currentPatchName);
    
    if (recentPatches.length > 0) {
        menuHtml += `<div class="patch-switcher-menu-divider"></div>`;
        menuHtml += `<div class="patch-switcher-section-label">Recent</div>`;
        menuHtml += recentPatches.map(p => {
            const isCloud = !!p.patchId;
            // Build meta spans like saved patches had: "3 devices" "5 cables" "2h ago"
            let metaHtml = '';
            if (typeof p.deviceCount === 'number' || typeof p.connectionCount === 'number' || p.timestamp) {
                metaHtml = '<div class="recent-patch-meta">';
                if (typeof p.deviceCount === 'number') {
                    metaHtml += `<span>${p.deviceCount} devices</span>`;
                }
                if (typeof p.connectionCount === 'number') {
                    metaHtml += `<span>${p.connectionCount} cables</span>`;
                }
                if (p.timestamp) {
                    metaHtml += `<span>${formatTimestamp(p.timestamp)}</span>`;
                }
                metaHtml += '</div>';
            }
            return `
                <div class="patch-switcher-menu-item recent-patch" onclick="switchToPatchFromMenu(event, '${escapeHtml(p.name)}', ${isCloud}, '${p.patchId || ''}')">
                    <div class="recent-patch-name">
                        <span class="icon">${isCloud ? '☁️' : '📄'}</span> ${escapeHtml(p.name)}
                    </div>
                    ${metaHtml}
                </div>
            `;
        }).join('');
    }
    
    menuEl.innerHTML = menuHtml;
    
    // Saved patches list removed - use Open dialog for full list with search
    list.innerHTML = '';
}

// ============= PATCH SWITCHING =============

/**
 * Switch to a different patch
 * @param {string} name - Patch name
 * @param {boolean} isCloud - Whether it's a cloud patch
 * @param {string} patchId - Patch ID (for cloud patches)
 */
async function switchToPatch(name, isCloud = false, patchId = '') {
    // Autosave is always on, so current state is already saved
    
    closePatchSwitcher();
    
    // Load from the appropriate storage based on isCloud flag
    const identifier = isCloud ? patchId : name;
    const success = await loadAndApplyPatchFn(identifier, false);
    
    if (success) {
        setCurrentPatchNameFn(name);
        recentPatchesService.addToRecentPatches(
            patchId,
            name,
            Date.now(),
            getInstanceCountFn(),
            getConnectionCountFn()
        );
        showToast(`Opened: ${name}`);
    } else {
        // Remove from recents if patch no longer exists
        recentPatchesService.removeFromRecents(patchId, name);
        showToast('Patch not found - removed from recents', true);
    }
}

/**
 * Delete a patch from the switcher
 * @param {Event} event - Click event
 * @param {string} name - Patch name
 * @param {boolean} isCloud - Whether it's a cloud patch
 * @param {string} patchId - Patch ID (for cloud patches)
 */
async function deletePatchFromSwitcher(event, name, isCloud = false, patchId = '') {
    event.stopPropagation();
    
    const confirmed = await showConfirmModalFn({
        title: 'Delete Patch',
        message: `Delete patch "${name}"?`,
        confirmText: 'Delete',
        danger: true
    });
    if (!confirmed) return;
    
    // Delete from the appropriate storage based on isCloud flag
    const identifier = isCloud ? patchId : name;
    const result = await storageService.deletePatch(identifier);
    
    if (result.success) {
        // Note: We don't remove from recent patches anymore - orphaned entries are harmless
        invalidatePatchesCache();
        renderPatchSwitcherList();
        showToast(`Deleted: ${name}`);
        
        // If we deleted the current patch, mark as untitled
        const currentPatchName = getCurrentPatchNameFn();
        if (name === currentPatchName) {
            setCurrentPatchNameFn('Untitled Patch');
        }
    } else {
        showToast('Failed to delete patch', true);
    }
}

/**
 * Create a new patch from switcher (legacy)
 * @param {Event} event - Click event
 */
function newPatchFromSwitcher(event) {
    event.stopPropagation();
    closePatchSwitcher();
    newPatchFn();
}

// ============= MENU ACTION HANDLERS =============

/**
 * New patch menu action
 * @param {Event} event - Click event
 */
function newPatchFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    newPatchFn();
}

/**
 * Save As menu action
 * @param {Event} event - Click event
 */
function saveAsFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    showSaveAsModalFn();
}

/**
 * Open menu action
 * @param {Event} event - Click event
 */
function openFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    showOpenStorageModalFn('open');
}

/**
 * Save Version menu action
 * @param {Event} event - Click event
 */
function saveVersionFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    showSaveVersionModalFn();
}

/**
 * Version History menu action
 * @param {Event} event - Click event
 */
function showVersionHistoryFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    showVersionHistoryModalFn();
}

/**
 * Save to File menu action (.pw export)
 * @param {Event} event - Click event
 */
function saveToFileFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    saveStateFn(); // Downloads as .pw file
}

/**
 * Save to Clipboard menu action
 * @param {Event} event - Click event
 */
function saveToClipboardFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    window.saveToClipboard();
}

/**
 * Import from File menu action
 * @param {Event} event - Click event
 */
function importFromFileFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    importFromFileFn();
}

/**
 * Import from Clipboard menu action
 * @param {Event} event - Click event
 */
function importFromClipboardFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    window.importFromClipboard();
}

/**
 * Export Share Code menu action
 * @param {Event} event - Click event
 */
function exportShareCodeFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    window.copyPatchShareString();
}

/**
 * Import Share Code menu action
 * @param {Event} event - Click event
 */
function importShareCodeFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    window.showImportShareStringDialog(true); // merge mode
}

/**
 * Switch to patch menu action
 * @param {Event} event - Click event
 * @param {string} name - Patch name
 * @param {boolean} isCloud - Whether it's a cloud patch
 * @param {string} patchId - Patch ID (for cloud patches)
 */
async function switchToPatchFromMenu(event, name, isCloud = false, patchId = '') {
    event.stopPropagation();
    closePatchSwitcher();
    
    // Autosave is always on, so no need to warn about unsaved changes
    
    // Load from the appropriate storage based on isCloud flag
    const identifier = isCloud ? patchId : name;
    const success = await loadAndApplyPatchFn(identifier, false);
    
    if (success) {
        setCurrentPatchNameFn(name);
        recentPatchesService.addToRecentPatches(
            patchId,
            name,
            Date.now(),
            getInstanceCountFn(),
            getConnectionCountFn()
        );
        showToast(`Opened: ${name}`);
    } else {
        // Remove from recents if patch no longer exists
        recentPatchesService.removeFromRecents(patchId, name);
        showToast('Patch not found - removed from recents', true);
    }
}

/**
 * Patch Settings menu action
 * @param {Event} event - Click event
 */
function patchSettingsFromMenu(event) {
    event.stopPropagation();
    closePatchSwitcher();
    showPatchSettingsModalFn();
}
